/**
 * CustomerVehiclePicker — "which customer, and which of their cars?"
 *
 * The two steps every form that touches a vehicle has to walk: find the customer (or
 * create one), then pick the vehicle (or add one). Extracted verbatim from
 * `BookingFormModal`, which owned them alone until quotes needed the same two steps.
 *
 * ⚠️ This is a MOVE, not a rewrite. The behaviour here is subtle and was arrived at
 * over several rounds of real use — the rego-ownership warning, the three result
 * cases, editing a vehicle in place, "add a different vehicle" for an existing
 * customer. Copying it into the quote form instead would have produced a second copy
 * that drifted, and the drift would show up as two screens disagreeing about who owns
 * a car.
 *
 * Both steps are presentational and fully controlled: they own only their own local
 * UI state (collapse, saving, lookup results) and hand every decision back through
 * callbacks. That is what lets the booking wizard and the quote form drive them with
 * completely different step counts and submit paths.
 *
 * Exports:
 *   CustomerLookupStep  — search by mobile / name / rego, or skip to manual entry
 *   CustomerVehicleStep — confirm the customer, choose or add the vehicle
 */

import React, { useState, useEffect } from 'react';
import {
    Search, Loader2, User, Car, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
    Phone, Mail, CheckCircle, AlertCircle, Plus, Pencil, Save, Building2, History,
} from 'lucide-react';
import {
    TRANSMISSION_OPTIONS,
    transmissionDisplay,
    TransmissionIcon,
    TransmissionBadge,
} from '../TransmissionIcons';
import { searchCustomers } from '../../services/customerApi';
import { updateVehicleApi, getVehicleByRegoApi, checkVehicleOwnershipApi } from '../../services/bookingApi';
import VehicleHistorySlideOut from '../VehicleHistorySlideOut';
import { normalizePhone, normalizeRego, customerSinceOf, REGO_STATES } from '../../utils/customerVehicle';

// ==================== Step 1 — Customer Lookup ====================

/**
 * Customer lookup with three optional inputs: Mobile, Name, Rego.
 *
 * Backed by GET /customers/search — partial, case-insensitive matching across
 * name (first OR last OR business), phone, and any of the customer's vehicle
 * regos. At least one of the three fields must be supplied to search; an
 * empty submit is a no-op. Multiple fields can be combined and the backend
 * ORs them.
 *
 * Because any single field can return multiple matches (two "John Smith"s,
 * or two customers whose vehicles' regos contain "ABC"), the UI handles
 * three result cases:
 *   - 0 results → "no customer found" panel with Create New Customer button
 *   - 1 result  → auto-select and advance to the next step
 *   - 2+ results → render a selectable list; staff pick the right one
 *
 * Layout is single-column, stacked, with large tap targets and an autoFocus
 * on Mobile — designed to work on a 380px tablet/phone viewport without
 * horizontal scroll.
 */
export const CustomerLookupStep = ({ onFound, onSkip, prefill }) => {
    /*
     * Seeded from a phone pre-booking when one is being converted. Without this the
     * workshop retypes a number, name and rego the bot already captured — and staff
     * re-keying a mobile by hand is exactly where a call-back goes to the wrong person.
     * Still editable: the values came from speech recognition and may be wrong.
     */
    const [phoneValue, setPhoneValue] = useState(prefill?.customerPhone ?? '');
    const [nameValue,  setNameValue]  = useState(prefill?.customerName  ?? '');
    const [regoValue,  setRegoValue]  = useState(prefill?.vehicleRego   ?? '');
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(null);
    const [results,    setResults]    = useState(null); // null = not searched yet; [] = searched, no matches

    const trimmedPhone = phoneValue.trim();
    const trimmedName  = nameValue.trim();
    const trimmedRego  = regoValue.trim();

    const hasPhone = !!trimmedPhone;
    const hasName  = !!trimmedName;
    const hasRego  = !!trimmedRego;

    // Any field alone is enough to search.
    const canSearch = hasPhone || hasName || hasRego;

    const clearAlerts = () => { setResults(null); setError(null); };

    const handleSearch = async () => {
        if (!canSearch) return;
        setLoading(true);
        setError(null);
        setResults(null);
        try {
            const params = {};
            if (hasPhone) params.phone = normalizePhone(trimmedPhone);
            if (hasName)  params.name  = trimmedName;
            // searchCustomers() applies normalizeRego (uppercase, strip
            // whitespace/dashes) before sending to the backend, so we can
            // pass through the user's raw input.
            if (hasRego)  params.rego  = trimmedRego;
            const response = await searchCustomers(params);
            const list = Array.isArray(response.data) ? response.data : [];
            if (list.length === 1) {
                // Single match — skip the picker and advance straight away.
                // Pass the searched rego so step 2 can pre-select the
                // matching vehicle if the customer has many.
                onFound(list[0], hasRego ? trimmedRego : null);
                return;
            }
            setResults(list);
        } catch (err) {
            setError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Pre-fill whatever the user typed into the new-customer form when they
    // give up on lookup and choose to create. Rego carries over to step 2's
    // vehicle section since the user already typed it once.
    const buildPrefill = () => {
        const prefill = {};
        if (hasPhone) prefill.customerPhone = trimmedPhone;
        if (hasName)  prefill.customerName  = trimmedName;
        if (hasRego)  prefill.vehicleRego   = normalizeRego(trimmedRego);
        return prefill;
    };

    const noMatches = results !== null && results.length === 0;
    const multipleMatches = results !== null && results.length > 1;

    return (
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
            <div>
                <h3 className="font-semibold text-gray-900 mb-1">Find Existing Customer</h3>
                <p className="text-sm text-gray-500">
                    Search by mobile, name, rego, or any combination.
                </p>
            </div>

            {/* ── Stacked inputs (mobile-friendly, single column) ── */}
            <div className="space-y-3">
                {/* Mobile */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                    <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="tel"
                            inputMode="tel"
                            value={phoneValue}
                            onChange={(e) => { setPhoneValue(e.target.value); clearAlerts(); }}
                            onKeyDown={(e) => e.key === 'Enter' && canSearch && handleSearch()}
                            placeholder="0412 345 678"
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-base"
                            autoFocus
                        />
                    </div>
                </div>

                {/* Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="text"
                            value={nameValue}
                            onChange={(e) => { setNameValue(e.target.value); clearAlerts(); }}
                            onKeyDown={(e) => e.key === 'Enter' && canSearch && handleSearch()}
                            placeholder="John Smith"
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-base"
                        />
                    </div>
                </div>

                {/* Rego */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rego</label>
                    <div className="relative">
                        <Car size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="text"
                            inputMode="text"
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                            value={regoValue}
                            // Force-uppercase as the user types so the picker
                            // hint matches the canonical form the backend stores.
                            onChange={(e) => { setRegoValue(e.target.value.toUpperCase()); clearAlerts(); }}
                            onKeyDown={(e) => e.key === 'Enter' && canSearch && handleSearch()}
                            placeholder="ABC123"
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-base font-mono tracking-wide uppercase"
                        />
                    </div>
                </div>
            </div>

            {/* ── Search button (full width on mobile) ── */}
            <button
                type="button" onClick={handleSearch}
                disabled={loading || !canSearch}
                className="w-full px-4 py-3 bg-violet-600 text-white rounded-lg font-medium text-sm hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Search
            </button>

            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle size={16} className="shrink-0" /> {error}
                </div>
            )}

            {/* ── Multiple matches — staff picks one ── */}
            {multipleMatches && (
                <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {results.length} matches — select one
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {results.map((c) => {
                            const isBusiness = c.customerType === 'BUSINESS';
                            const NameIcon = isBusiness ? Building2 : User;
                            return (
                                <button
                                    key={c.uuid}
                                    type="button"
                                    onClick={() => onFound(c, hasRego ? trimmedRego : null)}
                                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-violet-400 hover:bg-violet-50 transition-colors"
                                >
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <NameIcon size={14} className="text-gray-400 shrink-0" />
                                        <span className="font-medium text-sm text-gray-900 truncate">
                                            {c.fullName || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || '—'}
                                        </span>
                                        {isBusiness && (
                                            <span className="text-[10px] uppercase tracking-wide font-semibold text-violet-600 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                                                Business
                                            </span>
                                        )}
                                    </div>
                                    {c.phone && (
                                        <div className="flex items-center gap-2 text-xs text-gray-500 ml-6">
                                            <Phone size={11} className="shrink-0" />
                                            <span className="truncate">{c.phone}</span>
                                        </div>
                                    )}
                                    {Array.isArray(c.vehicles) && c.vehicles.length > 0 && (
                                        <div className="flex items-center gap-2 text-xs text-gray-500 ml-6 mt-0.5">
                                            <Car size={11} className="shrink-0" />
                                            <span className="truncate">
                                                {c.vehicles.length === 1
                                                    ? (c.vehicles[0].displayName || c.vehicles[0].regoNumber)
                                                    : `${c.vehicles.length} vehicles`}
                                            </span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── No matches — offer to create ── */}
            {noMatches && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                    <div className="flex items-start gap-2">
                        <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-amber-800">No customer found</p>
                            <p className="text-xs text-amber-600 mt-0.5">
                                No existing customer matches those details. You can create a new customer below.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onSkip({ prefill: buildPrefill() })}
                        className="w-full py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                        <Plus size={15} /> Create New Customer
                    </button>
                </div>
            )}

            <div className="pt-2 border-t border-gray-100">
                <button type="button" onClick={() => onSkip({ prefill: buildPrefill() })}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    Skip lookup — enter details manually
                </button>
            </div>
        </div>
    );
};

// ==================== Service Profile Selector (Step 2) ====================
// ==================== Step 2 — Customer & Vehicle ====================

export const CustomerVehicleStep = ({
                                 customerData, vehicles, selectedVehicleUuid, setSelectedVehicleUuid,
                                 addingNewVehicle, setAddingNewVehicle, formData, onChange, isEdit,
                                 onBack, onNext,
                                 editingVehicleUuid, setEditingVehicleUuid,
                                 anchorRef,
                                 onHeaderMouseDown,
                             }) => {
    const isExistingCustomer = !!customerData;
    const hasVehicles        = vehicles && vehicles.length > 0;

    const [vehicleSaving, setVehicleSaving]   = useState(false);
    const [vehicleSaved, setVehicleSaved]     = useState(false);
    const [vehicleError, setVehicleError]     = useState(null);
    const [vehicleLoading, setVehicleLoading] = useState(false);
    const [editingVehicleLabel, setEditingVehicleLabel] = useState(null);

    // Section collapse state — both default to expanded. Purely UI; not persisted.
    const [customerCollapsed, setCustomerCollapsed] = useState(false);
    const [vehicleCollapsed,  setVehicleCollapsed]  = useState(false);

    // Slide-out panel showing the selected vehicle's recent service history.
    const [historyOpen, setHistoryOpen] = useState(false);

    // ── Rego ownership warning ──
    //
    // Informational warning shown when staff type a rego in the new-vehicle
    // form that already exists in the org under a *different* customer.
    // The booking save itself handles the transfer (BookingService re-points
    // the existing Vehicle row's customer FK to the typed/looked-up customer);
    // this banner just tells the user what is about to happen so they don't
    // think they've made a mistake.
    //
    // The check is debounced (400ms) and only fires when:
    //   - the user is on the new-vehicle path (new customer OR existing
    //     customer with "add a different vehicle" selected),
    //   - they are NOT editing an existing vehicle row (rego changes on a
    //     known vehicle shouldn't warn),
    //   - the rego is at least 2 chars (avoids first-keystroke noise),
    //   - we have at least one of name/phone/email to identify the typed
    //     customer (the backend rejects requests without an identifier).
    const [ownershipWarning, setOwnershipWarning] = useState(null);

    const isNewVehiclePath = !isExistingCustomer
        || (addingNewVehicle && !editingVehicleUuid);

    // Identify which fields we have for the typed/looked-up customer. For an
    // existing customer (Step 1 found one) we take it straight off customerData;
    // for a new customer we take whatever has been typed into the form.
    const typedCustomerName = isExistingCustomer
        ? (customerData?.fullName || null)
        : (formData.customerType === 'BUSINESS'
            ? (formData.customerBusinessName || null)
            : (formData.customerName || null));
    const typedCustomerPhone = isExistingCustomer
        ? (customerData?.phone || null)
        : (formData.customerPhone || null);
    const typedCustomerEmail = isExistingCustomer
        ? (customerData?.email || null)
        : (formData.customerEmail || null);

    useEffect(() => {
        // Bail out paths — clear any stale warning and stop.
        if (!isNewVehiclePath) { setOwnershipWarning(null); return; }
        const regoRaw = formData.vehicleRego || '';
        const rego = normalizeRego(regoRaw);
        if (rego.length < 2) { setOwnershipWarning(null); return; }

        const hasIdentifier = !!(typedCustomerName || typedCustomerPhone || typedCustomerEmail);
        if (!hasIdentifier) { setOwnershipWarning(null); return; }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const result = await checkVehicleOwnershipApi({
                    rego,
                    customerName:  typedCustomerName,
                    customerPhone: typedCustomerPhone,
                    customerEmail: typedCustomerEmail,
                });
                if (cancelled) return;
                if (result?.vehicleExists && result?.ownershipConflict) {
                    setOwnershipWarning(result);
                } else {
                    setOwnershipWarning(null);
                }
            } catch (err) {
                // Soft-fail — never block the form on this advisory check.
                // 400s (missing identifier) and network errors both land here.
                if (!cancelled) setOwnershipWarning(null);
                console.warn('Vehicle ownership check failed:', err);
            }
        }, 400);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [
        isNewVehiclePath,
        formData.vehicleRego,
        typedCustomerName,
        typedCustomerPhone,
        typedCustomerEmail,
    ]);

    const handleEditVehicle = async (v) => {
        setSelectedVehicleUuid(null);
        setAddingNewVehicle(true);
        setEditingVehicleUuid(v.uuid);
        setEditingVehicleLabel(v.displayName || v.regoNumber || 'Vehicle');
        setVehicleSaved(false);
        setVehicleError(null);

        // Pre-fill rego immediately so the form isn't blank while loading
        onChange({ target: { name: 'vehicleRego',         value: v.regoNumber || '' } });
        onChange({ target: { name: 'vehicleRegoState',    value: v.regoState || 'VIC' } });
        onChange({ target: { name: 'vehicleMake',         value: '' } });
        onChange({ target: { name: 'vehicleModel',        value: '' } });
        onChange({ target: { name: 'vehicleYear',         value: '' } });
        onChange({ target: { name: 'vehicleVin',          value: '' } });
        onChange({ target: { name: 'vehicleEngineNumber', value: '' } });
        // Seed transmission from the customer-vehicle list immediately, mapping
        // legacy CVT/DCT into AUTOMATIC so the dropdown has a valid selection
        // before the by-rego API hydrates the rest.
        onChange({
            target: {
                name: 'vehicleTransmission',
                value: transmissionDisplay(v.transmission)?.bucket || ''
            }
        });

        if (!v.regoNumber) return;

        setVehicleLoading(true);
        try {
            const details = await getVehicleByRegoApi(v.regoNumber);
            onChange({ target: { name: 'vehicleRego',         value: details.rego || v.regoNumber || '' } });
            onChange({ target: { name: 'vehicleRegoState',    value: details.regoState || 'VIC' } });
            onChange({ target: { name: 'vehicleMake',         value: details.make || '' } });
            onChange({ target: { name: 'vehicleModel',        value: details.model || '' } });
            onChange({ target: { name: 'vehicleYear',         value: details.year ? String(details.year) : '' } });
            onChange({ target: { name: 'vehicleVin',          value: details.vin || '' } });
            onChange({ target: { name: 'vehicleEngineNumber', value: details.engineNumber || '' } });
            onChange({
                target: {
                    name: 'vehicleTransmission',
                    value: transmissionDisplay(details.transmission)?.bucket || ''
                }
            });
        } catch (err) {
            // Silently continue with blank fields — user can still fill manually
            console.warn('Failed to fetch vehicle details:', err);
        } finally {
            setVehicleLoading(false);
        }
    };

    const handleSaveVehicle = async () => {
        if (!editingVehicleUuid) return;
        setVehicleSaving(true);
        setVehicleError(null);
        setVehicleSaved(false);
        try {
            await updateVehicleApi(editingVehicleUuid, {
                rego:         formData.vehicleRego?.trim() || null,
                regoState:    formData.vehicleRegoState || null,
                make:         formData.vehicleMake?.trim() || null,
                model:        formData.vehicleModel?.trim() || null,
                year:         formData.vehicleYear ? parseInt(formData.vehicleYear) : null,
                vin:          formData.vehicleVin?.trim() || null,
                engineNumber: formData.vehicleEngineNumber?.trim() || null,
                transmission: formData.vehicleTransmission || null,
            });
            setVehicleSaved(true);
            setTimeout(() => setVehicleSaved(false), 3000);
        } catch (err) {
            setVehicleError(err.response?.data?.message || 'Failed to update vehicle');
        } finally {
            setVehicleSaving(false);
        }
    };

    const handleVehicleSelect = (uuid) => {
        setSelectedVehicleUuid(uuid);
        setAddingNewVehicle(false);
        setEditingVehicleUuid(null);
        setEditingVehicleLabel(null);
    };

    const handleAddNewVehicle = () => {
        setSelectedVehicleUuid(null);
        setAddingNewVehicle(true);
        setEditingVehicleUuid(null);
        setEditingVehicleLabel(null);
        ['vehicleRego', 'vehicleMake', 'vehicleModel', 'vehicleYear', 'vehicleVin', 'vehicleEngineNumber', 'vehicleTransmission'].forEach(field =>
            onChange({ target: { name: field, value: '' } })
        );
        onChange({ target: { name: 'vehicleRegoState', value: 'VIC' } });
    };

    const vehicleReady = () => {
        if (!isExistingCustomer) {
            // Required name field depends on the customer type:
            //   INDIVIDUAL → customerName (Full Name)
            //   BUSINESS   → customerBusinessName
            // Email is mandatory for both — matches backend
            // BookingController.validateCustomerInfo() which rejects blank email.
            // Transmission, make and model are mandatory on new vehicles (this is the
            // path that creates a fresh Vehicle row). Pre-existing rows may be null or
            // carry the old "Unknown" placeholder; this gate stops NEW ones joining them.
            const isBusiness = formData.customerType === 'BUSINESS';
            const nameOk = isBusiness
                ? !!formData.customerBusinessName
                : !!formData.customerName;
            return !!(
                nameOk
                && formData.customerPhone
                && formData.customerEmail
                && formData.vehicleRego
                && formData.vehicleMake
                && formData.vehicleModel
                && formData.vehicleTransmission
            );
        }
        // An existing customer with no email on file must supply one before proceeding —
        // the confirmation email is promised to phone callers and sent on creation.
        if (isExistingCustomer && !customerData?.email && !formData.customerEmail) {
            return false;
        }

        // Editing an existing vehicle counts as selected. Transmission is NOT
        // gated on edit — the row may have been created before the column
        // existed and the user might be there to fix something else entirely.
        if (editingVehicleUuid) return true;
        // "Add a different vehicle" for an existing customer is a new-vehicle
        // path, so transmission is required here as well.
        if (addingNewVehicle) {
            return !!(formData.vehicleRego && formData.vehicleMake
                && formData.vehicleModel && formData.vehicleTransmission);
        }
        return !!selectedVehicleUuid;
    };

    const canProceed = () => {
        if (!vehicleReady()) return false;
        // Service-profile gate has moved to Step 4 (Booking Details), where the
        // selector now lives.
        return true;
    };

    const ReadOnlyField = ({ label, value }) => (
        <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 font-medium">
                {value || <span className="text-gray-400 font-normal italic">—</span>}
            </div>
        </div>
    );

    return (
        <div className="p-5 space-y-5 overflow-y-auto flex-1">

            {/* ── CUSTOMER ── */}
            <div className={`rounded-xl p-4 border ${isExistingCustomer ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
                <button
                    type="button"
                    onClick={() => setCustomerCollapsed(c => !c)}
                    aria-expanded={!customerCollapsed}
                    className={`flex items-center gap-2 w-full text-left ${customerCollapsed ? '' : 'mb-3'}`}
                >
                    <User size={17} className={isExistingCustomer ? 'text-emerald-600' : 'text-blue-600'} />
                    <h3 className="font-semibold text-gray-900">Customer</h3>
                    {isExistingCustomer && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                            Existing
                        </span>
                    )}
                    {/* When collapsed, surface a quick summary so the section is still informative at a glance */}
                    {customerCollapsed && isExistingCustomer && customerData?.fullName && (
                        <span className="text-xs text-gray-600 truncate">
                            · {customerData.fullName}
                        </span>
                    )}
                    <span className="ml-auto text-gray-400">
                        {customerCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </span>
                </button>

                {!customerCollapsed && (isExistingCustomer ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-gray-500">
                                    {customerData.customerType === 'BUSINESS' ? 'Business Name' : 'Name'}
                                </label>
                                {customerSinceOf(customerData) && (
                                    <span className="text-[11px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                                        Customer since {customerSinceOf(customerData)}
                                    </span>
                                )}
                            </div>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 font-medium">
                                {customerData.fullName || <span className="text-gray-400 font-normal italic">—</span>}
                            </div>
                        </div>
                        <ReadOnlyField label="Phone" value={customerData.phone} />
                        {customerData.email ? (
                            <div className="col-span-2">
                                <ReadOnlyField label="Email" value={customerData.email} />
                            </div>
                        ) : (
                            /*
                             * This customer has no email on file — almost always one the
                             * voice bot created, since the bot is waived from collecting it.
                             * Ask now: the booking confirmation is emailed on creation, and
                             * a phone caller was told they would get one.
                             *
                             * Typing it here fills the BLANK on the customer record. It can
                             * never overwrite an address already on file, so this is not a
                             * back-door customer edit.
                             */
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="email" name="customerEmail" value={formData.customerEmail}
                                           onChange={onChange} placeholder="customer@email.com"
                                           className="w-full pl-10 pr-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                </div>
                                <p className="mt-1 text-xs text-amber-700">
                                    No email on file. Needed so the booking confirmation can be sent.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* ── Customer type toggle ── */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Customer Type</label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'INDIVIDUAL', label: 'Individual', Icon: User },
                                    { value: 'BUSINESS',   label: 'Business',   Icon: Building2 },
                                ].map(({ value, label, Icon }) => {
                                    const active = formData.customerType === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => onChange({ target: { name: 'customerType', value } })}
                                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                active
                                                    ? 'bg-violet-50 border-violet-400 text-violet-700'
                                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                        >
                                            <Icon size={15} />
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Name input (label + placeholder + state field switch by type) ── */}
                        {formData.customerType === 'BUSINESS' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Business Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="text" name="customerBusinessName" value={formData.customerBusinessName}
                                           onChange={onChange} placeholder="Test556 Pty Ltd"
                                           className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="text" name="customerName" value={formData.customerName}
                                           onChange={onChange} placeholder="John Smith"
                                           className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Mobile <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="tel" name="customerPhone" value={formData.customerPhone}
                                           onChange={onChange} placeholder="0412 345 678"
                                           className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="email" name="customerEmail" value={formData.customerEmail}
                                           onChange={onChange} placeholder="customer@email.com"
                                           className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── VEHICLE ── */}
            <div className="rounded-xl p-4 border border-gray-200 bg-gray-50">
                <button
                    type="button"
                    onClick={() => setVehicleCollapsed(c => !c)}
                    aria-expanded={!vehicleCollapsed}
                    className={`flex items-center gap-2 w-full text-left ${vehicleCollapsed ? '' : 'mb-3'}`}
                >
                    <Car size={17} className="text-violet-600" />
                    <h3 className="font-semibold text-gray-900">Vehicle</h3>
                    {/* Quick summary when collapsed — selected vehicle's display, or new-rego in progress */}
                    {vehicleCollapsed && (() => {
                        const selected = selectedVehicleUuid && !addingNewVehicle
                            ? vehicles.find(v => v.uuid === selectedVehicleUuid)
                            : null;
                        const summary = selected?.displayName
                            ?? (addingNewVehicle && formData.vehicleRego ? `New: ${formData.vehicleRego}` : null);
                        return summary ? (
                            <span className="text-xs text-gray-600 truncate">· {summary}</span>
                        ) : null;
                    })()}
                    <span className="ml-auto text-gray-400">
                        {vehicleCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </span>
                </button>

                {!vehicleCollapsed && (
                    <>
                        {isExistingCustomer && hasVehicles && (
                            <div className="space-y-2 mb-3">
                                {vehicles.map(v => {
                                    const isSelected = selectedVehicleUuid === v.uuid && !addingNewVehicle;
                                    const isEditing  = editingVehicleUuid === v.uuid && addingNewVehicle;

                                    return (
                                        <div key={v.uuid}
                                             className={`rounded-lg border-2 transition-colors ${
                                                 isEditing
                                                     ? 'border-violet-400 bg-violet-50/40'
                                                     : isSelected
                                                         ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-400'
                                                         : 'border-gray-200 bg-white hover:border-violet-300'
                                             }`}>
                                            {/* Radio row */}
                                            <div className="flex items-center gap-1.5">
                                                <button type="button" onClick={() => handleVehicleSelect(v.uuid)}
                                                        className="flex-1 text-left px-3 py-2.5 flex items-center gap-3">
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                                                ${isSelected && !isEditing ? 'border-violet-500' : 'border-gray-300'}`}>
                                                        {isSelected && !isEditing && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{v.displayName}</p>
                                                            <TransmissionBadge value={v.transmission} variant="pill" />
                                                        </div>
                                                        <p className="text-xs text-gray-500 font-mono">
                                                            {v.regoNumber}{v.regoState ? ` · ${v.regoState}` : ''}
                                                            {v.colour ? ` · ${v.colour}` : ''}
                                                        </p>
                                                    </div>
                                                </button>
                                                {isSelected && !isEditing && (
                                                    <button type="button"
                                                            onClick={() => setHistoryOpen(true)}
                                                            title="View service history"
                                                            className="mr-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 hover:border-violet-300 transition-colors shrink-0">
                                                        <History size={14} />
                                                        Service history
                                                    </button>
                                                )}
                                                {isEdit && !isEditing && (
                                                    <button type="button"
                                                            onClick={() => handleEditVehicle(v)}
                                                            title="Edit vehicle details"
                                                            className="p-2 mr-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors shrink-0">
                                                        <Pencil size={15} />
                                                    </button>
                                                )}
                                                {isEditing && (
                                                    <button type="button"
                                                            onClick={() => handleVehicleSelect(v.uuid)}
                                                            className="mr-2 text-xs text-gray-500 hover:text-gray-700 transition-colors shrink-0">
                                                        Close
                                                    </button>
                                                )}
                                            </div>

                                            {/* Expanded edit area */}
                                            {isEditing && (
                                                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-violet-200 relative">
                                                    {vehicleLoading && (
                                                        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-b-lg">
                                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                                <Loader2 size={16} className="animate-spin" /> Loading details…
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 mb-0.5">Rego</label>
                                                            <input type="text" name="vehicleRego" value={formData.vehicleRego}
                                                                   onChange={onChange} placeholder="ABC123" maxLength={7}
                                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 mb-0.5">State</label>
                                                            <select name="vehicleRegoState" value={formData.vehicleRegoState} onChange={onChange}
                                                                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm">
                                                                {REGO_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                        {[
                                                            { name: 'vehicleMake',  placeholder: 'Toyota' },
                                                            { name: 'vehicleModel', placeholder: 'Camry'  },
                                                        ].map(({ name, placeholder }) => (
                                                            <div key={name}>
                                                                <label className="block text-xs font-medium text-gray-600 mb-0.5 capitalize">
                                                                    {name.replace('vehicle', '')}
                                                                </label>
                                                                <input type="text" name={name} value={formData[name]}
                                                                       onChange={onChange} placeholder={placeholder}
                                                                       className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                                            </div>
                                                        ))}
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 mb-0.5">Year</label>
                                                            <input type="number" name="vehicleYear" value={formData.vehicleYear}
                                                                   onChange={onChange} placeholder="2020" min="1900" max="2030"
                                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 mb-0.5">VIN / Chassis</label>
                                                            <input type="text" name="vehicleVin" value={formData.vehicleVin}
                                                                   onChange={onChange} placeholder="e.g. MRHFC6650GT070203" maxLength={20}
                                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 mb-0.5">Engine Number</label>
                                                            <input type="text" name="vehicleEngineNumber" value={formData.vehicleEngineNumber}
                                                                   onChange={onChange} placeholder="e.g. R18Z16150605" maxLength={20}
                                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Transmission</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {TRANSMISSION_OPTIONS.map(opt => {
                                                                const active = formData.vehicleTransmission === opt.value;
                                                                return (
                                                                    <button
                                                                        key={opt.value}
                                                                        type="button"
                                                                        onClick={() => onChange({ target: { name: 'vehicleTransmission', value: active ? '' : opt.value } })}
                                                                        className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                                                                            active
                                                                                ? (opt.value === 'MANUAL'
                                                                                    ? 'bg-amber-50 border-amber-400 text-amber-700'
                                                                                    : 'bg-sky-50 border-sky-400 text-sky-700')
                                                                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                                                        }`}
                                                                    >
                                                                        <TransmissionIcon bucket={opt.value} size={12} />
                                                                        {opt.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveVehicle}
                                                        disabled={vehicleSaving || !formData.vehicleRego}
                                                        className="w-full py-2 bg-violet-600 text-white rounded-lg font-medium text-sm hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                                    >
                                                        {vehicleSaving
                                                            ? <Loader2 size={16} className="animate-spin" />
                                                            : vehicleSaved
                                                                ? <CheckCircle size={16} />
                                                                : <Save size={16} />
                                                        }
                                                        {vehicleSaving ? 'Saving…' : vehicleSaved ? 'Vehicle Updated' : 'Update Vehicle Details'}
                                                    </button>
                                                    {vehicleError && (
                                                        <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                                                            <AlertCircle size={14} className="shrink-0" /> {vehicleError}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Add a different vehicle — always available in the radio list */}
                                <button type="button" onClick={handleAddNewVehicle}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex items-center gap-3
                                ${addingNewVehicle && !editingVehicleUuid
                                            ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-400'
                                            : 'border-dashed border-gray-300 bg-white hover:border-violet-300'
                                        }`}
                                >
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                                ${addingNewVehicle && !editingVehicleUuid ? 'border-violet-500' : 'border-gray-300'}`}>
                                        {addingNewVehicle && !editingVehicleUuid && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm font-medium text-violet-600">
                                        <Plus size={14} /> Add a different vehicle
                                    </div>
                                </button>
                            </div>
                        )}

                        {isExistingCustomer && !hasVehicles && !addingNewVehicle && (
                            <div className="text-sm text-gray-500 mb-3 p-3 bg-white border border-dashed border-gray-200 rounded-lg">
                                No vehicles on file for this customer.{' '}
                                <button type="button" onClick={handleAddNewVehicle}
                                        className="text-violet-600 hover:underline font-medium">
                                    Add one now
                                </button>
                            </div>
                        )}

                        {/* New vehicle form — only when adding brand new (not editing existing) */}
                        {(!isExistingCustomer || (addingNewVehicle && !editingVehicleUuid)) && (
                            <div className="space-y-3">

                                {/* ── Rego ownership warning ──
                                    Informational only. The booking save will
                                    re-point the existing Vehicle row's
                                    customer FK to the typed/looked-up
                                    customer, so this banner just tells staff
                                    what's about to happen — there's no
                                    confirmation button because clicking
                                    Continue IS the confirmation. */}
                                {ownershipWarning && (
                                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-amber-900">
                                                    This rego is already on file
                                                </p>
                                                <p className="text-xs text-amber-800 mt-0.5">
                                                    <span className="font-mono font-semibold">
                                                        {normalizeRego(formData.vehicleRego)}
                                                    </span>
                                                    {ownershipWarning.vehicleDescription
                                                        ? ` (${ownershipWarning.vehicleDescription})`
                                                        : ''}
                                                    {' is currently registered to '}
                                                    <span className="font-semibold">
                                                        {ownershipWarning.existingCustomerName || 'another customer'}
                                                    </span>
                                                    {ownershipWarning.existingCustomerPhone
                                                        ? ` · ${ownershipWarning.existingCustomerPhone}`
                                                        : ''}
                                                    .
                                                </p>
                                                <p className="text-xs text-amber-900 mt-1.5">
                                                    If you continue, this vehicle will be transferred to new customer and service history will follow it to the new owner.
                                                    You can check the ownership transfer with the old owner if you wish.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rego <span className="text-red-500">*</span>
                                        </label>
                                        <input type="text" name="vehicleRego" value={formData.vehicleRego}
                                               onChange={onChange} placeholder="ABC123" maxLength={7}
                                               className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                        <select name="vehicleRegoState" value={formData.vehicleRegoState} onChange={onChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm">
                                            {REGO_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {[
                                        { name: 'vehicleMake',  placeholder: 'Toyota' },
                                        { name: 'vehicleModel', placeholder: 'Camry'  },
                                    ].map(({ name, placeholder }) => (
                                        <div key={name}>
                                            {/* Make and model are mandatory server-side on a
                                                new vehicle — they used to default to the
                                                string "Unknown" and leave placeholder rows
                                                nobody could tell from real data. */}
                                            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                                                {name.replace('vehicle', '')} <span className="text-red-500">*</span>
                                            </label>
                                            <input type="text" name={name} value={formData[name]}
                                                   onChange={onChange} placeholder={placeholder}
                                                   className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                        </div>
                                    ))}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                        <input type="number" name="vehicleYear" value={formData.vehicleYear}
                                               onChange={onChange} placeholder="2020" min="1900" max="2030"
                                               className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">VIN / Chassis</label>
                                        <input type="text" name="vehicleVin" value={formData.vehicleVin}
                                               onChange={onChange} placeholder="e.g. MRHFC6650GT070203" maxLength={20}
                                               className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label>
                                        <input type="text" name="vehicleEngineNumber" value={formData.vehicleEngineNumber}
                                               onChange={onChange} placeholder="e.g. R18Z16150605" maxLength={20}
                                               className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm uppercase font-mono" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Transmission <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {TRANSMISSION_OPTIONS.map(opt => {
                                            const active = formData.vehicleTransmission === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => onChange({ target: { name: 'vehicleTransmission', value: active ? '' : opt.value } })}
                                                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                        active
                                                            ? (opt.value === 'MANUAL'
                                                                ? 'bg-amber-50 border-amber-400 text-amber-700'
                                                                : 'bg-sky-50 border-sky-400 text-sky-700')
                                                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <TransmissionIcon bucket={opt.value} size={14} />
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── SERVICE PROFILE / ON DEMAND selector moved to Step 4 (Booking Details) ── */}

            {/* Navigation */}
            <div className="flex gap-3 pt-2">
                {!isEdit && (
                    <button type="button" onClick={onBack}
                            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors">
                        <ChevronLeft size={16} /> Back
                    </button>
                )}
                <button type="button" onClick={onNext} disabled={!canProceed()}
                        className="flex-1 py-2.5 bg-violet-600 text-white rounded-xl font-medium text-sm hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                    Continue <ChevronRight size={16} />
                </button>
            </div>

            {/* Service-history slide-out for the currently selected vehicle. */}
            {(() => {
                const selectedVehicle = selectedVehicleUuid && !addingNewVehicle
                    ? vehicles.find(v => v.uuid === selectedVehicleUuid)
                    : null;
                const label = selectedVehicle
                    ? `${selectedVehicle.displayName || ''}${selectedVehicle.regoNumber ? ` · ${selectedVehicle.regoNumber}` : ''}`.trim()
                    : null;
                return (
                    <VehicleHistorySlideOut
                        open={historyOpen}
                        onClose={() => setHistoryOpen(false)}
                        vehicleUuid={selectedVehicleUuid}
                        vehicleLabel={label}
                        anchorRef={anchorRef}
                        onHeaderMouseDown={onHeaderMouseDown}
                    />
                );
            })()}
        </div>
    );
};
